import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded'
import DnsRoundedIcon from '@mui/icons-material/DnsRounded'
import DomainRoundedIcon from '@mui/icons-material/DomainRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded'
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import {
  Box,
  Chip,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { agentOpsPipelinePath, paths } from '@/routes/paths'
import { platformSurfaces } from '@/super-admin/theme'

interface NavItem {
  label: string
  path: string
  icon: ReactNode
  /** Coincidencia exacta (para rutas raíz con subrutas). */
  end?: boolean
}

interface NavGroup {
  title: string
  items: NavItem[]
}

const AGENT_OPS_ITEMS: NavItem[] = [
  { label: 'Overview', path: paths.superAdminAgentOps, icon: <HubRoundedIcon fontSize="small" />, end: true },
  { label: 'Runs', path: paths.superAdminAgentOpsRuns, icon: <ListAltRoundedIcon fontSize="small" /> },
  { label: 'Recolección', path: agentOpsPipelinePath('collection'), icon: <CloudDownloadRoundedIcon fontSize="small" /> },
  { label: 'Evidencia', path: agentOpsPipelinePath('evidence'), icon: <FactCheckRoundedIcon fontSize="small" /> },
  { label: 'Inteligencia', path: agentOpsPipelinePath('intelligence'), icon: <PsychologyRoundedIcon fontSize="small" /> },
  { label: 'Predicción', path: agentOpsPipelinePath('prediction'), icon: <TimelineRoundedIcon fontSize="small" /> },
  { label: 'Decisiones y aprobaciones', path: paths.superAdminAgentOpsApprovals, icon: <GavelRoundedIcon fontSize="small" /> },
  { label: 'Evaluaciones y aprendizaje', path: paths.superAdminAgentOpsEvaluations, icon: <SchoolRoundedIcon fontSize="small" /> },
  { label: 'Controles', path: paths.superAdminAgentOpsControls, icon: <TuneRoundedIcon fontSize="small" /> },
]

/**
 * Navegación exclusiva de la consola de plataforma (administración SaaS).
 * Nunca comparte menú con el cliente (routes/navigation.ts).
 */
const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Plataforma',
    items: [{ label: 'Resumen', path: paths.superAdmin, icon: <SpaceDashboardRoundedIcon fontSize="small" />, end: true }],
  },
  {
    title: 'Clientes',
    items: [
      { label: 'Organizaciones', path: paths.superAdminOrganizations, icon: <DomainRoundedIcon fontSize="small" /> },
      { label: 'Usuarios', path: paths.superAdminUsers, icon: <GroupRoundedIcon fontSize="small" /> },
      { label: 'Planes y contratos', path: paths.superAdminPlans, icon: <ReceiptLongRoundedIcon fontSize="small" /> },
    ],
  },
  {
    title: 'Operación',
    items: [
      { label: 'Fuentes', path: paths.superAdminSources, icon: <DnsRoundedIcon fontSize="small" /> },
      { label: 'Costos IA', path: paths.superAdminCosts, icon: <PaidRoundedIcon fontSize="small" /> },
      { label: 'Salud', path: paths.superAdminHealth, icon: <MonitorHeartRoundedIcon fontSize="small" /> },
    ],
  },
  {
    title: 'Control',
    items: [
      { label: 'Auditoría', path: paths.superAdminAudit, icon: <FactCheckRoundedIcon fontSize="small" /> },
      { label: 'Configuración', path: paths.superAdminSettings, icon: <SettingsRoundedIcon fontSize="small" /> },
    ],
  },
]

export const SIDEBAR_SUPER_ADMIN_WIDTH = 256

const itemSx = {
  mx: 1,
  borderRadius: 1,
  borderLeft: '3px solid transparent',
  '&.active': {
    bgcolor: 'rgba(245, 158, 11, 0.10)',
    color: 'primary.light',
    borderLeftColor: 'primary.main',
    '& .MuiListItemIcon-root': { color: 'primary.main' },
  },
}

function NavEntry({ item, dense = false }: { item: NavItem; dense?: boolean }) {
  return (
    <ListItemButton component={NavLink} to={item.path} end={item.end} sx={{ ...itemSx, py: dense ? 0.4 : 0.75, pl: dense ? 4 : 2 }}>
      <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>{item.icon}</ListItemIcon>
      <ListItemText slotProps={{ primary: { fontSize: dense ? '0.82rem' : '0.875rem', fontWeight: 500 } }}>
        {item.label}
      </ListItemText>
    </ListItemButton>
  )
}

function GroupTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{ px: 3, pt: 2, pb: 0.5, display: 'block', letterSpacing: 1, fontWeight: 700, color: 'text.disabled' }}
    >
      {children}
    </Typography>
  )
}

export function SidebarSuperAdmin() {
  const location = useLocation()
  const inAgentOps = location.pathname.startsWith(paths.superAdminAgentOps)
  const [agentOpsOpen, setAgentOpsOpen] = useState(true)
  const open = agentOpsOpen || inAgentOps

  return (
    <Box
      component="nav"
      aria-label="Navegación de plataforma"
      sx={{
        width: SIDEBAR_SUPER_ADMIN_WIDTH,
        flexShrink: 0,
        height: '100vh',
        position: 'fixed',
        borderRight: `1px solid ${platformSurfaces.border}`,
        bgcolor: platformSurfaces.sidebar,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={0.75} sx={{ px: 3, pt: 3, pb: 2.5, borderBottom: `1px solid ${platformSurfaces.border}` }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: 'primary.main' }} />
          <Typography sx={{ fontWeight: 800, letterSpacing: 2, fontSize: '0.95rem' }}>GOVIA</Typography>
        </Stack>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Platform Console
        </Typography>
        <Chip
          label="OPERADOR DE PLATAFORMA"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ alignSelf: 'flex-start', fontSize: '0.62rem', letterSpacing: 0.6, height: 20 }}
        />
      </Stack>

      <Box sx={{ overflowY: 'auto', flex: 1, pb: 2 }}>
        {NAV_GROUPS.slice(0, 2).map((group) => (
          <Box key={group.title}>
            <GroupTitle>{group.title.toUpperCase()}</GroupTitle>
            <List dense disablePadding>
              {group.items.map((item) => (
                <NavEntry key={item.path} item={item} />
              ))}
            </List>
          </Box>
        ))}

        <GroupTitle>AGENT OPS</GroupTitle>
        <List dense disablePadding>
          <ListItemButton
            onClick={() => setAgentOpsOpen((v) => !v)}
            aria-expanded={open}
            aria-label="Mostrar u ocultar secciones de Agent Ops"
            sx={{ ...itemSx, py: 0.75, pl: 2, ...(inAgentOps ? { color: 'primary.light' } : {}) }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: inAgentOps ? 'primary.main' : 'text.secondary' }}>
              <HubRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { fontSize: '0.875rem', fontWeight: 600 } }}>Agent Ops</ListItemText>
            {open ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
          </ListItemButton>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List dense disablePadding>
              {AGENT_OPS_ITEMS.map((item) => (
                <NavEntry key={item.path} item={item} dense />
              ))}
            </List>
          </Collapse>
        </List>

        {NAV_GROUPS.slice(2).map((group) => (
          <Box key={group.title}>
            <GroupTitle>{group.title.toUpperCase()}</GroupTitle>
            <List dense disablePadding>
              {group.items.map((item) => (
                <NavEntry key={item.path} item={item} />
              ))}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
